import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Search, ArrowUpRight, ListFilter, Sparkles, Send, Inbox, Trophy,
  FileSpreadsheet, Paperclip, Link2, BadgeCheck,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { money, statusMeta, shortDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RfqTypeBadge } from "@/components/RfqType";
import { PriceAutomaticallyButton } from "@/components/PriceAutomaticallyButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "accepted", "rejected", "cancelled"];

const grab = (url, filename) => {
  const token = localStorage.getItem("matiqs_token");
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Could not download that file"));
};

export default function Tracker() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("incoming");
  const [rfqs, setRfqs] = useState(null);
  const [opps, setOpps] = useState(null);
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [catEmpty, setCatEmpty] = useState(false);
  const trackerCreated = useRef({});

  const load = () => {
    api.get("/rfqs").then((r) => setRfqs(r.data)).catch((e) => {
      toast.error(apiError(e.response?.data?.detail));
      setRfqs([]);
    });
    api.get("/vendor/opportunities").then((r) => setOpps(r.data.opportunities)).catch(() => setOpps([]));
    api.get("/stats").then((r) => setCatEmpty(r.data.catalogue_count === 0)).catch(() => {});
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (opps && opps.length === 0 && tab === "incoming") setTab("quotes");
  }, [opps]);

  const rows = useMemo(() => {
    if (!rfqs) return [];
    const needle = q.trim().toLowerCase();
    return rfqs.filter((r) => {
      const okStatus = filter === "all" || (r.status || "draft") === filter;
      const okText =
        !needle ||
        (r.name || "").toLowerCase().includes(needle) ||
        (r.buyer_name || "").toLowerCase().includes(needle) ||
        (r.quote_ref || "").toLowerCase().includes(needle);
      return okStatus && okText;
    });
  }, [rfqs, q, filter]);

  const setStatus = async (r, status) => {
    try {
      await api.put(`/rfqs/${r.id}`, { status });
      setRfqs((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(`${r.quote_ref} marked ${statusMeta[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const priceIt = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await api.post(`/vendor/opportunities/${o.id}/start`);
      if (data.ai_skipped) {
        toast.warning(`Items loaded without AI pricing — your ${data.plan_name} plan's ${data.quota} automatic quotes are used up this month`);
      } else if (!data.reused) {
        toast.success(`Priced ${data.matched} of ${data.total} lines from your catalogue`);
      }
      navigate(`/rfq/${data.rfq_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start the quotation");
    } finally {
      setBusy("");
    }
  };

  const sendQuote = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await api.post(`/vendor/opportunities/${o.id}/submit`);
      toast.success(`Quotation sent to ${o.buyer_company} — ${o.currency} ${Number(data.total).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the quotation");
    } finally {
      setBusy("");
    }
  };

  if (!rfqs || !opps) {
    return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const newCount = opps.filter((o) => o.my_status === "invited").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-slate-100">
            RFQ Tracker
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            RFQs contractors sent you, and every quotation you have built for them.
          </p>
        </div>
        {tab === "quotes" && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="tracker-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search quote, contractor, ref"
                className="pl-9 h-11 w-full sm:w-64"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="tracker-filter" className="h-11 w-40 bg-card gap-2">
                <ListFilter className="w-4 h-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{statusMeta[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="inline-flex p-1 bg-secondary rounded-xl mb-5">
        {[
          { id: "incoming", label: `Incoming RFQs${newCount ? ` (${newCount} new)` : ""}` },
          { id: "quotes", label: "My quotations" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tracker-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === t.id ? "bg-card shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "incoming" ? (
        opps.length === 0 ? (
          <div data-testid="no-opportunities" className="bg-card border border-border rounded-xl p-12 text-center">
            <Inbox className="w-9 h-9 text-muted-foreground mx-auto" />
            <p className="font-semibold text-slate-900 dark:text-slate-100 mt-4">No RFQs waiting on you</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Contractors find vendors by trade. Keep your profile, trades and documents current and your catalogue loaded to win more invitations.
            </p>
            <Button variant="outline" onClick={() => navigate("/settings")} className="mt-5 font-semibold">Review my profile</Button>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left font-semibold px-4 py-3">RFQ Ref</th>
                    <th className="text-left font-semibold px-4 py-3 min-w-[200px]">RFQ</th>
                    <th className="text-left font-semibold px-4 py-3">Contractor</th>
                    <th className="text-left font-semibold px-4 py-3">Date Received</th>
                    <th className="text-left font-semibold px-4 py-3">Files</th>
                    <th className="text-right font-semibold px-4 py-3">My Quote</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-right font-semibold px-4 py-3 min-w-[220px]">Action</th>
                  </tr>
                </thead>
                <tbody data-testid="incoming-body">
                  {opps.map((o, i) => (
                    <tr key={o.id} data-testid={`incoming-row-${i}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          <button data-testid={`incoming-ref-${i}`} onClick={() => navigate(`/incoming/${o.id}`)}
                                  className="hover:text-primary transition-colors">
                            {o.rfq_ref || "—"}
                          </button>
                          {o.rfq_type === "job_on_hand" && <RfqTypeBadge type={o.rfq_type} testid={`incoming-type-${i}`} />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          {o.title}
                          {o.awarded_to_me && (
                            <span data-testid={`won-badge-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
                              <Trophy className="w-3 h-3" /> Won
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{[o.project, `${o.item_count} items`, o.due_date && `due ${o.due_date}`].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{o.buyer_company}</span>
                          {o.buyer_verified && (
                            <span data-testid={`incoming-verified-${i}`} title="Verified contractor" className="inline-flex items-center text-emerald-600 dark:text-emerald-400">
                              <BadgeCheck className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-testid={`received-${i}`} className="px-4 py-3 text-muted-foreground whitespace-nowrap">{shortDate(o.invited_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {o.boq && (
                            <button data-testid={`incoming-boq-${i}`} title={o.boq.filename}
                                    onClick={() => grab(`/buyer/rfqs/${o.id}/boq/file`, o.boq.filename)}
                                    className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-colors">
                              <FileSpreadsheet className="w-4 h-4" />
                            </button>
                          )}
                          {(o.attachments || []).map((a, j) => (
                            <button key={a.id} data-testid={`incoming-att-${i}-${j}`} title={a.filename}
                                    onClick={() => grab(`/buyer/rfqs/${o.id}/attachments/${a.id}/file`, a.filename)}
                                    className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-colors">
                              <Paperclip className="w-4 h-4" />
                            </button>
                          ))}
                          {o.package_url && (
                            <a href={o.package_url} target="_blank" rel="noreferrer" title="Full package"
                               data-testid={`incoming-package-${i}`}
                               className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-colors">
                              <Link2 className="w-4 h-4" />
                            </a>
                          )}
                          {!o.boq && !(o.attachments || []).length && !o.package_url && <span className="text-muted-foreground">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                        {o.my_total > 0 ? money(o.my_total, o.currency) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide ${o.my_status === "quoted" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" : o.my_status === "invited" ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                          {o.my_status}
                        </span>
                        {o.bafo?.status === "requested" && (
                          <span data-testid={`bafo-badge-${i}`} className="block mt-1 px-2 py-0.5 rounded-md bg-brand text-brand-foreground text-[10px] font-bold uppercase tracking-wide text-center">
                            Best & final
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" data-testid={`review-rfq-${i}`} onClick={() => navigate(`/incoming/${o.id}`)} className="h-9">
                            Review
                          </Button>
                          {o.item_count === 0 ? (
                            <span className="text-xs text-muted-foreground">Quote by email</span>
                          ) : o.quote_rfq_id ? (
                            <>
                              <Button variant="outline" data-testid={`open-quote-${i}`} onClick={() => navigate(`/rfq/${o.quote_rfq_id}`)} className="h-9">
                                Open
                              </Button>
                              <Button data-testid={`send-quote-${i}`} onClick={() => sendQuote(o)} disabled={busy === o.id} className="h-9 gap-1.5 font-semibold">
                                {busy === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {o.my_status === "quoted" ? "Resend" : "Send"}
                              </Button>
                            </>
                          ) : (
                            <PriceAutomaticallyButton
                              testid={`price-it-${i}`}
                              className="h-9 gap-1.5 font-semibold"
                              align="end"
                              catEmpty={catEmpty}
                              getRfqId={async () => {
                                const { data } = await api.post(`/vendor/opportunities/${o.id}/start`, null, { params: { mode: "manual" } });
                                trackerCreated.current[o.id] = data.rfq_id;
                                return data.rfq_id;
                              }}
                              onCatalogue={async () => { await priceIt(o); }}
                              onPriced={() => { const rid = trackerCreated.current[o.id]; if (rid) navigate(`/rfq/${rid}`); }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-3">Quote Ref</th>
                  <th className="text-left font-semibold px-4 py-3 min-w-[220px]">Quotation</th>
                  <th className="text-left font-semibold px-4 py-3">Contractor</th>
                  <th className="text-center font-semibold px-3 py-3">Rev</th>
                  <th className="text-left font-semibold px-4 py-3">Created</th>
                  <th className="text-left font-semibold px-4 py-3">Date Sent</th>
                  <th className="text-right font-semibold px-4 py-3">Total</th>
                  <th className="text-left font-semibold px-4 py-3 w-40">Status</th>
                  <th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="tracker-body">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-muted-foreground">
                      No quotations match this view.
                    </td>
                  </tr>
                ) : rows.map((r, idx) => {
                  const sm = statusMeta[r.status] || statusMeta.draft;
                  return (
                    <tr key={r.id} data-testid={`tracker-row-${idx}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{r.quote_ref}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{r.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.buyer_name || "—"}</td>
                      <td className="px-3 py-3 text-center">
                        <span data-testid={`tracker-revision-${idx}`} className="inline-block px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[11px] font-bold">
                          {r.revision || 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{shortDate(r.created_at)}</td>
                      <td data-testid={`tracker-sent-${idx}`} className="px-4 py-3 text-muted-foreground whitespace-nowrap">{shortDate(r.sent_at)}</td>
                      <td className="px-4 py-3 text-right font-mono font-medium whitespace-nowrap">{money(r.total, r.currency)}</td>
                      <td className="px-4 py-3">
                        <Select value={r.status || "draft"} onValueChange={(v) => setStatus(r, v)}>
                          <SelectTrigger data-testid={`tracker-status-${idx}`} className={`h-8 w-32 text-xs font-semibold border-0 ${sm.cls}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card">
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{statusMeta[s]?.label || s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3">
                        <button
                          data-testid={`tracker-open-${idx}`}
                          onClick={() => navigate(`/rfq/${r.id}`)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                          title="Open quote"
                        >
                          <ArrowUpRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
